// The catalogue of reports the per-report access model governs (ENG-1595). Frontend-only: the
// backend stores the permissions as an opaque JSON blob and never resolves these ids, so there is
// no enum to keep in lockstep.
//
// Every report a user can navigate to belongs here. The nav gate is fail-open by design
// (reports.component.tsx — grouping nodes and operational views have no id and must not be hidden),
// so a report missing from this file is silently ungoverned: always visible, always downloadable,
// never scoped, with nothing failing to tell you. Adding a report to the module means adding it here.
//
// In-component tabs of a registered report deliberately inherit its row rather than getting their
// own: appointment-status-by-referral-source and appointment-created-status-by-referral-source are
// tabs of appointment-status, and sub-views like revenue-breakdown/clinic or hearing-aids/warranty
// inherit via the URL walk in resolveReportIdFromPathname.
import {
  ACCOUNTS_RECEIVABLE_REPORT_VIEW,
  AR_SNAPSHOT_REPORT,
  CHS_REPORT_FEATURE,
  COMMISSIONS_BREAKDOWN_REPORT,
  COMMISSIONS_REPORT,
  GENERAL_LEDGER_REPORT,
  INSURER_CPT_CODES_USAGE_REPORT,
  INSURER_CREDIT_LEDGER,
  MANAGED_CARE_AGING_REPORT,
  MANAGED_CARE_HEARING_AID_UNITS_REPORT,
  NEW_PATIENT_APPOINTMENTS_REPORT,
  ONLINE_SCHEDULED_APPOINTMENTS_REPORTS,
  OPEN_INVENTORY_REPORT_FEATURE,
  PATIENT_CPT_REPORT,
  PERSONAL_EVENTS_REPORT,
  PHYSICIAN_REFERRALS_REPORT,
  PRIVATE_PAY_AGING_REPORT,
  PROFITABILITY_REPORT,
  RECURRING_PAYMENTS_REPORT,
  REVENUE_BY_INSURER_REPORT,
  SALES_MANAGEMENT_REPORT,
  SURVEY_REPORT_DASHBOARD,
  TESTED_NOT_SOLD_REPORT,
  TOTAL_SALES_FINANCES_REPORT,
  UNANSWERED_TEXTS_FEATURE
} from "constants/feature-flags";

// Ordered to match the reports module (features/reports/reports.component.tsx MENU), grouped by
// category — this order drives the matrix tab order and the within-tab row order.
export type ReportId =
  // Patients
  | "patients-report"
  | "survey-report"
  | "onboarding-forms-report"
  | "visit-tracker-report"
  | "insurance-policies-report"
  // Appointments
  | "appointments-report"
  | "cancelled-and-no-show-appointments-report"
  | "tested-not-sold-report"
  | "open-opportunities-report"
  | "appointments-from-web-scheduler-report"
  | "new-patient-appointments-report"
  | "capacity-report"
  | "appointment-status-report"
  | "appointment-completion-report"
  | "physician-referrals-report"
  | "personal-events-report"
  // Sales
  | "sale"
  | "finances"
  | "sale-items"
  | "returned-line-items"
  | "exchanged-line-items"
  | "general-ledger-report"
  | "profitability"
  | "insurer-cpt-code"
  | "patient-cpt-code"
  | "sales-by-zip-code-report"
  | "sales-by-appointment-type-report"
  | "sale-tax"
  | "commissions-report"
  | "commissions-breakdown"
  // Staff
  | "opportunity-sales-report"
  | "opportunity-sales-appointment-type-report"
  | "staff-metrics-report"
  | "staff-net-payments-report"
  // Accounts receivable / payable
  | "accounts-receivable-report"
  | "invoices-receivable"
  | "insurer-receivable"
  | "managed-care-receivable"
  | "managed-care-hearing-aid-units"
  | "ar-snapshot"
  | "private-pay-aging"
  | "managed-care-party-aging"
  | "accounts-payable-report"
  // Payments
  | "payment"
  | "payment-method-breakdown"
  | "patient-credits"
  | "payment-requests"
  | "recurring-payments-report"
  | "adjustment"
  | "adjustment-breakdown"
  | "refunds"
  | "insurer-credit-ledger"
  // Revenue
  | "revenue-breakdown-report"
  | "appointments-revenue-by-referral"
  | "revenue-by-product-type"
  | "revenue-by-product-category"
  | "revenue-by-managed-care-report"
  | "revenue-by-insurer-report"
  // Inventory
  | "patient-product"
  | "chs-report"
  | "order-item"
  // Other
  | "claims-report"
  | "alerts-report"
  | "unanswered-texts-report"
  | "discarded";

export type ReportCategory =
  | "accounts_receivable"
  | "appointments"
  | "inventory"
  | "other"
  | "patients"
  | "payments"
  | "revenue"
  | "sales"
  | "staff";

export type ReportRegistryEntry = {
  id: ReportId;
  label: string;
  category: ReportCategory;
  isFinancial: boolean;
  /** Route slug under /reports — how a nav entry is matched back to this report. */
  route: string;
  /**
   * The report's staff/provider filter key, when it has one. `self_only` pins this filter to the
   * user. Where a report exposes two, this is the "whose work this is" one — never `created_by`.
   */
  staffFilter?: string;
  /** The report's clinic filter key, when it has one. `clinic_scope` restricts this filter. */
  clinicFilter?: string;
  /**
   * The registered report whose nav item this one nests under. Drives the matrix's indentation,
   * the expand-only nav container, and the route guard's redirect from a denied parent to its
   * first granted child. Only set where BOTH ends are registered reports and the child's route
   * mounts at /reports/<parent.route>/<child.route> (asserted by the parity test).
   */
  parentId?: ReportId;
  /**
   * The LD flag gating this report's nav item and route, copied from reports.component.tsx.
   * Recorded so a redirect never lands on a flag-off route — that `<Route>` never mounts and the
   * page renders blank. This is NOT the gate itself; the nav and routes still own that.
   *
   * A few reports are gated structurally instead (a conditional spread or ternary around the nav
   * item rather than a `featureFlag:` key) — `invoices-receivable` and `revenue-by-insurer`. They
   * are recorded here too, so redirect targets stay honest.
   */
  featureFlag?: string;
};

export const REPORT_REGISTRY: Record<ReportId, ReportRegistryEntry> = {
  // Patients
  "patients-report": {
    id: "patients-report",
    route: "patients",
    // No staffFilter/clinicFilter on purpose: the Patients report's accordion filters are its own
    // system (preferredProvider / preferred_clinic mean the patient's preference, not "records
    // this user created"), so self-only/clinic-scope locks don't apply — View/Download only.
    label: "Patients",
    category: "patients",
    isFinancial: false
  },
  "survey-report": {
    id: "survey-report",
    route: "survey-report",
    parentId: "patients-report",
    featureFlag: SURVEY_REPORT_DASHBOARD,
    label: "Survey Feedback",
    category: "patients",
    isFinancial: false
  },
  "onboarding-forms-report": {
    id: "onboarding-forms-report",
    route: "onboarding-forms",
    parentId: "patients-report",
    staffFilter: "requested_by",
    clinicFilter: "clinic",
    label: "Onboarding Forms",
    category: "patients",
    isFinancial: false
  },
  "visit-tracker-report": {
    id: "visit-tracker-report",
    route: "visit-tracker-report",
    parentId: "patients-report",
    clinicFilter: "clinic",
    label: "Visit Tracker",
    category: "patients",
    isFinancial: false
  },
  "insurance-policies-report": {
    id: "insurance-policies-report",
    route: "insurance-policies",
    parentId: "patients-report",
    clinicFilter: "clinic",
    label: "Insurance Policies",
    category: "patients",
    isFinancial: false
  },
  // Appointments
  "appointments-report": {
    id: "appointments-report",
    route: "appointments",
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Appointments",
    category: "appointments",
    isFinancial: false
  },
  "cancelled-and-no-show-appointments-report": {
    id: "cancelled-and-no-show-appointments-report",
    route: "cancelled-and-no-show-appointments",
    parentId: "appointments-report",
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Cancelled/No-Show Appointments",
    category: "appointments",
    isFinancial: false
  },
  "tested-not-sold-report": {
    id: "tested-not-sold-report",
    route: "tested-not-sold",
    parentId: "appointments-report",
    featureFlag: TESTED_NOT_SOLD_REPORT,
    staffFilter: "staff_member",
    clinicFilter: "clinic",
    label: "Not Converted",
    category: "appointments",
    isFinancial: false
  },
  "open-opportunities-report": {
    id: "open-opportunities-report",
    route: "open-opportunities",
    parentId: "appointments-report",
    staffFilter: "staff_member",
    clinicFilter: "clinic",
    label: "Open Opportunities",
    category: "appointments",
    isFinancial: false
  },
  "appointments-from-web-scheduler-report": {
    id: "appointments-from-web-scheduler-report",
    route: "appointments-from-web-scheduler",
    parentId: "appointments-report",
    featureFlag: ONLINE_SCHEDULED_APPOINTMENTS_REPORTS,
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "From Web Scheduler",
    category: "appointments",
    isFinancial: false
  },
  // Governed View/Download only (like Patients / Staff Net Payments): its filters are a bespoke
  // RHF form, not the shared custom-filters pickers the lock system binds to, so declaring
  // staffFilter/clinicFilter here would claim locks that never apply.
  "new-patient-appointments-report": {
    id: "new-patient-appointments-report",
    route: "new-patient-appointments",
    parentId: "appointments-report",
    featureFlag: NEW_PATIENT_APPOINTMENTS_REPORT,
    label: "New Patients",
    category: "appointments",
    isFinancial: false
  },
  "capacity-report": {
    id: "capacity-report",
    route: "capacity",
    staffFilter: "staff_member",
    clinicFilter: "clinic",
    label: "Capacity",
    category: "appointments",
    isFinancial: false
  },
  "appointment-status-report": {
    id: "appointment-status-report",
    route: "appointment-status",
    staffFilter: "staff_member",
    clinicFilter: "clinic",
    label: "Appointment Status",
    category: "appointments",
    isFinancial: false
  },
  "appointment-completion-report": {
    id: "appointment-completion-report",
    route: "appointment-completion",
    staffFilter: "staff_member",
    clinicFilter: "clinic",
    label: "Appointment Completion",
    category: "appointments",
    isFinancial: false
  },
  "physician-referrals-report": {
    id: "physician-referrals-report",
    route: "physician-referrals",
    featureFlag: PHYSICIAN_REFERRALS_REPORT,
    clinicFilter: "clinic",
    label: "Physician Referrals",
    category: "appointments",
    isFinancial: false
  },
  "personal-events-report": {
    id: "personal-events-report",
    route: "personal-events",
    featureFlag: PERSONAL_EVENTS_REPORT,
    staffFilter: "staff_member",
    clinicFilter: "clinic",
    label: "Personal Events",
    category: "appointments",
    isFinancial: false
  },
  // Sales
  sale: {
    id: "sale",
    route: "sales",
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Sales",
    category: "sales",
    isFinancial: true
  },
  finances: {
    id: "finances",
    route: "finances",
    parentId: "sale",
    featureFlag: TOTAL_SALES_FINANCES_REPORT,
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Finances",
    category: "sales",
    isFinancial: true
  },
  "sale-items": {
    id: "sale-items",
    route: "line-items",
    parentId: "sale",
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Line Items",
    category: "sales",
    isFinancial: true
  },
  "returned-line-items": {
    id: "returned-line-items",
    route: "returned-line-items",
    parentId: "sale",
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Returned Line Items",
    category: "sales",
    isFinancial: true
  },
  "exchanged-line-items": {
    id: "exchanged-line-items",
    route: "exchanged-line-items",
    parentId: "sale",
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Exchanged Line Items",
    category: "sales",
    isFinancial: true
  },
  "general-ledger-report": {
    id: "general-ledger-report",
    route: "general-ledger",
    parentId: "sale",
    featureFlag: GENERAL_LEDGER_REPORT,
    label: "General Ledger",
    category: "sales",
    isFinancial: true
  },
  profitability: {
    id: "profitability",
    route: "profitability",
    parentId: "sale",
    featureFlag: PROFITABILITY_REPORT,
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Profitability",
    category: "sales",
    isFinancial: true
  },
  "insurer-cpt-code": {
    id: "insurer-cpt-code",
    route: "insurer-cpt-codes-usage",
    parentId: "sale",
    featureFlag: INSURER_CPT_CODES_USAGE_REPORT,
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Insurer CPT Codes",
    category: "sales",
    isFinancial: true
  },
  "patient-cpt-code": {
    id: "patient-cpt-code",
    route: "patient-cpt-codes-usage",
    parentId: "sale",
    featureFlag: PATIENT_CPT_REPORT,
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Patient CPT Codes",
    category: "sales",
    isFinancial: true
  },
  "sales-by-zip-code-report": {
    id: "sales-by-zip-code-report",
    route: "sales-by-zip-code",
    parentId: "sale",
    clinicFilter: "clinic",
    label: "Sales by Zip Code",
    category: "sales",
    isFinancial: true
  },
  "sales-by-appointment-type-report": {
    id: "sales-by-appointment-type-report",
    route: "sales-by-appointment-type",
    parentId: "sale",
    clinicFilter: "clinic",
    label: "Sales by Appointment Type",
    category: "sales",
    isFinancial: true
  },
  "sale-tax": {
    id: "sale-tax",
    route: "taxes",
    label: "Tax",
    category: "sales",
    isFinancial: true
  },
  "commissions-report": {
    id: "commissions-report",
    route: "commissions",
    featureFlag: COMMISSIONS_REPORT,
    staffFilter: "staff",
    label: "Commissions",
    category: "sales",
    isFinancial: true
  },
  // Route slugs must be unique module-wide (REPORT_ID_BY_ROUTE is flat and the nav filter matches
  // by slug alone), so this report's slug is "calculated-commissions", not "breakdown" — that slug
  // belongs to Payment Method Breakdown.
  "commissions-breakdown": {
    id: "commissions-breakdown",
    route: "calculated-commissions",
    parentId: "commissions-report",
    featureFlag: COMMISSIONS_BREAKDOWN_REPORT,
    staffFilter: "staff",
    label: "Calculated Commissions",
    category: "sales",
    isFinancial: true
  },
  // Staff
  "opportunity-sales-report": {
    id: "opportunity-sales-report",
    route: "sales-management",
    parentId: "sale",
    featureFlag: SALES_MANAGEMENT_REPORT,
    label: "Sales Management",
    category: "staff",
    isFinancial: true
  },
  "opportunity-sales-appointment-type-report": {
    id: "opportunity-sales-appointment-type-report",
    route: "sales-management-appointment-types",
    parentId: "sale",
    featureFlag: SALES_MANAGEMENT_REPORT,
    staffFilter: "provider",
    label: "Sales Management (Appointment Types)",
    category: "staff",
    isFinancial: true
  },
  "staff-metrics-report": {
    id: "staff-metrics-report",
    route: "staff-metrics",
    staffFilter: "provider",
    label: "Staff Metrics",
    category: "staff",
    isFinancial: true
  },
  // Governed View/Download only, deliberately no `clinicFilter`: this report's endpoint reads the
  // clinic param as `int(request.query_params.get('clinic'))`
  // (billing/serializers/staff_net_payments_report.py), so it accepts exactly one id. A clinic scope
  // of two would seed `clinic=1,2` and 500 the report. The backend already narrows it natively for
  // `show_own_clinics_only` users. Same posture as the Patients report and the Discarded group.
  "staff-net-payments-report": {
    id: "staff-net-payments-report",
    route: "staff-net-payments",
    parentId: "staff-metrics-report",
    label: "Staff Net Payments",
    category: "staff",
    isFinancial: true
  },
  // Accounts receivable / payable
  "accounts-receivable-report": {
    id: "accounts-receivable-report",
    route: "accounts-receivable",
    staffFilter: "preferred_provider",
    clinicFilter: "clinic",
    label: "Accounts Receivable",
    category: "accounts_receivable",
    isFinancial: false
  },
  "invoices-receivable": {
    id: "invoices-receivable",
    route: "invoices-receivable",
    parentId: "accounts-receivable-report",
    featureFlag: ACCOUNTS_RECEIVABLE_REPORT_VIEW,
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Invoices Receivable",
    category: "accounts_receivable",
    isFinancial: false
  },
  "insurer-receivable": {
    id: "insurer-receivable",
    route: "insurer-receivable",
    parentId: "accounts-receivable-report",
    clinicFilter: "clinic",
    label: "Insurer Receivable",
    category: "accounts_receivable",
    isFinancial: false
  },
  "managed-care-receivable": {
    id: "managed-care-receivable",
    route: "managed-care-receivable",
    parentId: "accounts-receivable-report",
    clinicFilter: "clinic",
    label: "Managed Care Receivable",
    category: "accounts_receivable",
    isFinancial: false
  },
  "managed-care-hearing-aid-units": {
    id: "managed-care-hearing-aid-units",
    route: "managed-care-hearing-aid-units",
    parentId: "accounts-receivable-report",
    featureFlag: MANAGED_CARE_HEARING_AID_UNITS_REPORT,
    label: "Managed Care Hearing Aid Units",
    category: "accounts_receivable",
    isFinancial: false
  },
  "ar-snapshot": {
    id: "ar-snapshot",
    route: "ar-snapshot",
    parentId: "accounts-receivable-report",
    featureFlag: AR_SNAPSHOT_REPORT,
    label: "AR Snapshot",
    category: "accounts_receivable",
    isFinancial: false
  },
  "private-pay-aging": {
    id: "private-pay-aging",
    route: "private-pay-aging",
    parentId: "accounts-receivable-report",
    featureFlag: PRIVATE_PAY_AGING_REPORT,
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Private Pay Aging",
    category: "accounts_receivable",
    isFinancial: false
  },
  "managed-care-party-aging": {
    id: "managed-care-party-aging",
    route: "managed-care-aging",
    parentId: "accounts-receivable-report",
    featureFlag: MANAGED_CARE_AGING_REPORT,
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Managed Care Aging",
    category: "accounts_receivable",
    isFinancial: false
  },
  "accounts-payable-report": {
    id: "accounts-payable-report",
    route: "accounts-payable",
    label: "Accounts Payable",
    category: "accounts_receivable",
    isFinancial: false
  },
  // Payments
  payment: {
    id: "payment",
    route: "payments",
    staffFilter: "staff",
    clinicFilter: "clinic",
    label: "Payments",
    category: "payments",
    isFinancial: true
  },
  "payment-method-breakdown": {
    id: "payment-method-breakdown",
    route: "breakdown",
    parentId: "payment",
    clinicFilter: "clinic",
    label: "Payment Method Breakdown",
    category: "payments",
    isFinancial: true
  },
  "patient-credits": {
    id: "patient-credits",
    route: "patient-credits",
    clinicFilter: "clinic",
    label: "Credits",
    category: "payments",
    isFinancial: true
  },
  "payment-requests": {
    id: "payment-requests",
    route: "online-payment-requests",
    clinicFilter: "clinic",
    label: "Online Payment Requests",
    category: "payments",
    isFinancial: false
  },
  "recurring-payments-report": {
    id: "recurring-payments-report",
    route: "recurring-payments",
    featureFlag: RECURRING_PAYMENTS_REPORT,
    label: "Recurring Payments",
    category: "payments",
    isFinancial: true
  },
  adjustment: {
    id: "adjustment",
    route: "adjustments",
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Adjustments",
    category: "payments",
    isFinancial: true
  },
  "adjustment-breakdown": {
    id: "adjustment-breakdown",
    route: "type",
    parentId: "adjustment",
    clinicFilter: "clinic",
    label: "Adjustment Breakdown",
    category: "payments",
    isFinancial: true
  },
  refunds: {
    id: "refunds",
    route: "refunds",
    clinicFilter: "clinic",
    label: "Refunds",
    category: "payments",
    isFinancial: true
  },
  "insurer-credit-ledger": {
    id: "insurer-credit-ledger",
    route: "insurer-credit-ledger",
    featureFlag: INSURER_CREDIT_LEDGER,
    // Money the practice holds for insurers (SN-19061). The nav additionally limits it to
    // INSURER_CREDIT_LEDGER_GROUPS because every endpoint behind it 403s for anyone else; the
    // "entries" sub-view inherits this row through the URL walk. No staff/clinic filter — its
    // filters are insurer and "include insurers at zero". Not in the finalized
    // Financial-Reports-Only list (financial-only-access.test.ts pins that set); widening it is a
    // product decision, and the role gate above already decides who sees the ledger.
    label: "Insurance Credit",
    category: "payments",
    isFinancial: false
  },
  // Revenue
  "revenue-breakdown-report": {
    id: "revenue-breakdown-report",
    route: "revenue-breakdown",
    label: "Revenue Breakdown",
    category: "revenue",
    isFinancial: true
  },
  "appointments-revenue-by-referral": {
    id: "appointments-revenue-by-referral",
    route: "sales-by-referral-source",
    parentId: "revenue-breakdown-report",
    clinicFilter: "clinic",
    label: "Revenue by Referral Source",
    category: "revenue",
    isFinancial: true
  },
  "revenue-by-product-type": {
    id: "revenue-by-product-type",
    route: "revenue-by-product-type",
    parentId: "revenue-breakdown-report",
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Revenue by Product",
    category: "revenue",
    isFinancial: true
  },
  "revenue-by-product-category": {
    id: "revenue-by-product-category",
    route: "revenue-by-product-category",
    parentId: "revenue-breakdown-report",
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Revenue by Product Category",
    category: "revenue",
    isFinancial: true
  },
  "revenue-by-managed-care-report": {
    id: "revenue-by-managed-care-report",
    route: "revenue-by-managed-care",
    parentId: "revenue-breakdown-report",
    clinicFilter: "clinic",
    label: "Revenue by Managed Care",
    category: "revenue",
    isFinancial: true
  },
  "revenue-by-insurer-report": {
    id: "revenue-by-insurer-report",
    route: "revenue-by-insurer",
    parentId: "revenue-breakdown-report",
    featureFlag: REVENUE_BY_INSURER_REPORT,
    clinicFilter: "clinic",
    label: "Revenue by Insurer",
    category: "revenue",
    isFinancial: true
  },
  // Inventory
  "patient-product": {
    id: "patient-product",
    route: "hearing-aids",
    clinicFilter: "patient_clinic",
    label: "Hearing Aids / Warranty",
    category: "inventory",
    isFinancial: false
  },
  "chs-report": {
    id: "chs-report",
    route: "chs",
    featureFlag: CHS_REPORT_FEATURE,
    clinicFilter: "clinic",
    label: "CHS",
    category: "inventory",
    isFinancial: true
  },
  "order-item": {
    id: "order-item",
    route: "patient-orders",
    featureFlag: OPEN_INVENTORY_REPORT_FEATURE,
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Patient Orders",
    category: "inventory",
    isFinancial: false
  },
  // Other
  "claims-report": {
    id: "claims-report",
    route: "claims",
    staffFilter: "provider",
    clinicFilter: "clinic",
    label: "Claims",
    category: "other",
    isFinancial: false
  },
  "alerts-report": {
    id: "alerts-report",
    route: "alerts",
    clinicFilter: "clinic",
    label: "Alerts",
    category: "other",
    isFinancial: false
  },
  "unanswered-texts-report": {
    id: "unanswered-texts-report",
    route: "unanswered-texts",
    clinicFilter: "clinic",
    staffFilter: "assignee",
    label: "Unanswered Texts",
    category: "other",
    isFinancial: false,
    featureFlag: UNANSWERED_TEXTS_FEATURE
  },
  discarded: {
    id: "discarded",
    route: "discarded",
    // One row governs the whole Discarded group (deleted sales, payments, adjustments, refunds,
    // line items, appointments). Its children reuse main-report slugs, so the URL resolver
    // short-circuits to this entry; no staffFilter/clinicFilter — the six children's filter sets
    // are heterogeneous, and this surface is about visibility of deleted financial records.
    label: "Discarded Reports",
    category: "other",
    isFinancial: true
  }
};

export const REPORT_IDS = Object.keys(REPORT_REGISTRY) as ReportId[];

/** Route slug → report id, so a reports-module nav entry can be matched back to its permission. */
export const REPORT_ID_BY_ROUTE: Record<string, ReportId> = REPORT_IDS.reduce(
  (acc, id) => {
    acc[REPORT_REGISTRY[id].route] = id;
    return acc;
  },
  {} as Record<string, ReportId>
);

/**
 * Which report the current URL is showing, or undefined outside the reports module.
 *
 * Only segments after a `reports` segment are considered — several registry slugs (`payments`,
 * `refunds`, `taxes`, `alerts`, …) also exist as routes elsewhere in the app (billing, QuickBooks,
 * patients), and matching those would apply report locks to unrelated pages. The walk runs from
 * the most specific segment outward so a sub-view (`revenue-breakdown/clinic`,
 * `hearing-aids/warranty`) inherits its parent report.
 *
 * The Discarded group is the one exception to the walk: its children reuse main-report slugs
 * (`discarded/sales`, `discarded/payments`, …), so they resolve to the group itself — otherwise
 * denying Sales would also block Discarded → Sales, and Discarded could never be governed alone.
 */
export const resolveReportIdFromPathname = (
  pathname: string
): ReportId | undefined => {
  const segments = pathname.split("/").filter(Boolean);
  const start = segments.indexOf("reports");
  if (start === -1) return undefined;

  const reportSegments = segments.slice(start + 1);
  if (reportSegments.includes("discarded")) {
    return REPORT_ID_BY_ROUTE["discarded"];
  }
  for (let i = reportSegments.length - 1; i >= 0; i -= 1) {
    const match = REPORT_ID_BY_ROUTE[reportSegments[i]];
    if (match) return match;
  }
  return undefined;
};

/** True when the pathname is the reports module root — `/reports`, with no report after it. */
export const isReportsIndexPathname = (pathname: string): boolean => {
  const segments = pathname.split("/").filter(Boolean);
  return segments[segments.length - 1] === "reports";
};

/**
 * The report the bare `/reports` index actually renders, which depends on the viewer's role —
 * reports.routes.tsx branches the index element three ways. Without this the index resolves to no
 * report at all and the route guard fails open, which is how a user denied every report still got
 * the Patients report at `/reports`.
 *
 * Keep in step with the index route's ternary in reports.routes.tsx (and the `isFallbackPath` flags
 * in reports.component.tsx, which mirror the same branching for nav highlighting).
 */
export const indexReportIdFor = ({
  isAdminOrBusinessOwner,
  haveViewReportsPermission
}: {
  isAdminOrBusinessOwner: boolean;
  haveViewReportsPermission: boolean;
}): ReportId => {
  if (isAdminOrBusinessOwner) return "patients-report";
  if (haveViewReportsPermission)
    return "cancelled-and-no-show-appointments-report";
  return "tested-not-sold-report";
};

/** Where a report lives: `/reports/<parent>/<route>` for a sub-report, `/reports/<route>` otherwise. */
export const reportPath = (reportId: ReportId): string => {
  const entry = REPORT_REGISTRY[reportId];
  const parent = entry.parentId ? REPORT_REGISTRY[entry.parentId] : undefined;
  return parent
    ? `/reports/${parent.route}/${entry.route}`
    : `/reports/${entry.route}`;
};

/**
 * The first report this user may actually open, in module order — the landing spot when the report
 * they asked for is denied.
 *
 * Skips reports whose LD flag is off: their `<Route>` never mounts, so redirecting there would land
 * on a blank page. Returns undefined when every report is denied, which the caller turns into the
 * permission page.
 */
export const firstGrantedReportPath = (
  canView: (reportId: ReportId) => boolean,
  flags: Record<string, unknown>
): string | undefined => {
  const granted = REPORT_IDS.find((reportId) => {
    const { featureFlag } = REPORT_REGISTRY[reportId];
    const flagOn = !featureFlag || Boolean(flags[featureFlag]);
    return flagOn && canView(reportId);
  });

  return granted ? reportPath(granted) : undefined;
};

/** Registered sub-reports per parent, in registry (module) order. */
export const CHILD_IDS_BY_PARENT: Partial<Record<ReportId, ReportId[]>> =
  REPORT_IDS.reduce(
    (acc, id) => {
      const parentId = REPORT_REGISTRY[id].parentId;
      if (parentId) {
        (acc[parentId] ??= []).push(id);
      }
      return acc;
    },
    {} as Partial<Record<ReportId, ReportId[]>>
  );

export const REPORT_CATEGORY_LABELS: Record<ReportCategory, string> = {
  accounts_receivable: "Accounts Receivable",
  appointments: "Appointments",
  inventory: "Inventory",
  other: "Other",
  patients: "Patients",
  payments: "Payments",
  revenue: "Revenue",
  sales: "Sales",
  staff: "Staff"
};

/**
 * The reports a "Financial Reports Only Access" user (`User.financial_reports_only_access`, the
 * accountant/bookkeeper restriction — Intercom 215475571171654) may open. Derived from each
 * registry row rather than hand-enumerated so a new report is judged by its own markers:
 * financial reports plus the whole Accounts Receivable category (whose rows are deliberately
 * `isFinancial: false` — see the registry comments), minus the Staff category (operational
 * performance data, not accounting).
 */
const isFinancialOnlyReport = ({
  category,
  isFinancial
}: ReportRegistryEntry): boolean =>
  category !== "staff" && (isFinancial || category === "accounts_receivable");

export const FINANCIAL_ONLY_REPORT_IDS: ReadonlySet<ReportId> = new Set(
  REPORT_IDS.filter((id) => isFinancialOnlyReport(REPORT_REGISTRY[id]))
);

/**
 * Categories whose every report sits behind the legacy "Access to Financial Reports" switch
 * (`User.can_see_manufacturer_cost`), plus the Payments rows that do. That switch is read by the
 * `canSeeFinancialReports` blocks in reports.component.tsx (nav) and reports.routes.tsx (routes)
 * in EVERY mode, Advanced included, so a report granted in the matrix stays hidden while it is off.
 * Online Payment Requests, Recurring Payments and Insurance Credit are mounted outside those blocks.
 * Keep this in step with them: the admin matrix uses it to say which grants the switch is blocking.
 */
const FINANCIAL_SWITCH_CATEGORIES: ReadonlySet<ReportCategory> = new Set([
  "sales",
  "staff",
  "accounts_receivable",
  "revenue"
]);

const PAYMENTS_OUTSIDE_FINANCIAL_SWITCH: ReadonlySet<ReportId> = new Set([
  "payment-requests",
  "recurring-payments-report",
  "insurer-credit-ledger"
]);

export const FINANCIAL_SWITCH_REPORT_IDS: ReadonlySet<ReportId> = new Set(
  REPORT_IDS.filter((id) => {
    const { category } = REPORT_REGISTRY[id];
    return (
      FINANCIAL_SWITCH_CATEGORIES.has(category) ||
      (category === "payments" && !PAYMENTS_OUTSIDE_FINANCIAL_SWITCH.has(id))
    );
  })
);

/**
 * Nav/route slugs OUTSIDE the registry (deliberately ungoverned by per-report access) that a
 * financial-only user may still reach. Everything else ungoverned — Birthdates above all, a
 * patient PHI listing — is blocked for them, inverting the registry's usual fail-open contract.
 */
export const FINANCIAL_ONLY_UNGOVERNED_ROUTES: ReadonlySet<string> = new Set([
  "insurer-credit-ledger"
]);

/**
 * Whether a reports-module pathname that resolves to NO registered report is still allowed for a
 * financial-only user. The first segment after `reports` decides — sub-views (e.g.
 * `insurer-credit-ledger/entries`) inherit their section's answer, and the bare index is allowed
 * because the route guard already redirects it to the first granted report.
 */
export const isFinancialOnlyAllowedUngovernedPathname = (
  pathname: string
): boolean => {
  const segments = pathname.split("/").filter(Boolean);
  const start = segments.indexOf("reports");
  if (start === -1) return true;
  const first = segments[start + 1];
  return first === undefined || FINANCIAL_ONLY_UNGOVERNED_ROUTES.has(first);
};

/** A report's clinic filter once resolved: every clinic, or only `selectedClinicIds`. */
export type ClinicScope = "all" | "selected";

/**
 * A report's saved clinic scope. `default` follows the person's rule (`ReportPermissions.clinics`),
 * so one setting covers every report and a report differs only where it was set on its own.
 */
export type EntryClinicScope = ClinicScope | "default";

/**
 * The person's clinic rule for their reports. `assigned` is live: it resolves to whichever clinics
 * the user is assigned to (Clinics tab) when a report loads, never to a saved list.
 */
export type PersonClinicScope = "all" | "assigned" | "selected";

export type PersonClinics = {
  scope: PersonClinicScope;
  selected_clinic_ids: number[];
};

export type ReportAccessMode = "simple" | "advanced";

/** One report's settings inside `User.report_permissions.reports`. */
export type ReportPermissionEntry = {
  view: boolean;
  download: boolean;
  // self_only and clinic_scope are frontend-only: they describe/label the intent, and the backend
  // never scopes a report queryset on them.
  self_only: boolean;
  clinic_scope: EntryClinicScope;
  selected_clinic_ids: number[];
};

/**
 * The whole `User.report_permissions` blob. An absent/empty object means "never configured" — the
 * user keeps full report access (mirrors `can_see_manufacturer_cost` defaulting to on). A report
 * present in `reports` is authoritative and overrides group membership.
 *
 * `clinics` is the person's clinic rule, followed by every report whose `clinic_scope` is `default`
 * and by every report with no entry. A blob saved before it existed has no `clinics` key and no
 * `default` entries, and reads exactly as it did: every report keeps its own `all` / `selected`.
 */
export type ReportPermissions = {
  mode?: ReportAccessMode;
  reports?: Partial<Record<ReportId, ReportPermissionEntry>>;
  clinics?: PersonClinics;
};
